import { z } from 'zod';
import {
  AGENT_KEYS,
  AGENT_RUN_STATUSES,
  AGENT_RUN_TRIGGERS,
  AREA_MEMBER_ROLES,
  AREA_TYPES,
  BRAND_SCOPES,
  CHANNEL_TARGET_STATUSES,
  CHANNEL_TYPES,
  COMPANIES,
  CONTENT_FORMATS,
  CONTENT_STATUSES,
  KPI_SOURCES,
  NOTIFICATION_CHANNELS,
  NOTIFICATION_TYPES,
  PILLARS,
  PROFILE_DOCUMENT_KINDS,
  PROFILE_DOCUMENT_TYPES,
  PUBLISH_MODES,
  SIGNAL_STATUSES,
  TENDER_BUYER_TYPES,
  TENDER_NO_BID_REASONS,
  TENDER_NOTICE_TYPES,
  TENDER_STATUSES,
  USER_ROLES,
  ORGANIZATION_TYPES,
  PRODUCT_HOLDING_STATUSES,
  HOLDING_CONFIDENCES,
  INTERACTION_TYPES,
  SOCIAL_PLATFORMS,
  SOCIAL_ACCOUNT_KINDS,
  SOCIAL_METRIC_SOURCES,
} from './enums.js';

/** Zod enums generated from the shared const tuples, so there is one source of truth. */
export const zUserRole = z.enum(USER_ROLES);
export const zAreaType = z.enum(AREA_TYPES);
export const zCompany = z.enum(COMPANIES);
export const zAreaMemberRole = z.enum(AREA_MEMBER_ROLES);
export const zPillar = z.enum(PILLARS);
export const zContentFormat = z.enum(CONTENT_FORMATS);
export const zContentStatus = z.enum(CONTENT_STATUSES);
export const zChannelType = z.enum(CHANNEL_TYPES);
export const zPublishMode = z.enum(PUBLISH_MODES);
export const zChannelTargetStatus = z.enum(CHANNEL_TARGET_STATUSES);
export const zAgentKey = z.enum(AGENT_KEYS);
export const zAgentRunTrigger = z.enum(AGENT_RUN_TRIGGERS);
export const zAgentRunStatus = z.enum(AGENT_RUN_STATUSES);
export const zNotificationType = z.enum(NOTIFICATION_TYPES);
export const zNotificationChannel = z.enum(NOTIFICATION_CHANNELS);
export const zKpiSource = z.enum(KPI_SOURCES);
export const zSignalStatus = z.enum(SIGNAL_STATUSES);
export const zSocialPlatform = z.enum(SOCIAL_PLATFORMS);
export const zSocialAccountKind = z.enum(SOCIAL_ACCOUNT_KINDS);
export const zSocialMetricSource = z.enum(SOCIAL_METRIC_SOURCES);

const cuid = z.string().min(1);
/** Accepts both ISO strings and Date objects; JSON bodies always carry strings. */
const isoDate = z.union([z.string().datetime({ offset: true }), z.string().date()]);

// ── Auth ─────────────────────────────────────────────────────────────────────

export const loginSchema = z.object({
  email: z.string().email('Ugyldig e-postadresse'),
  password: z.string().min(1, 'Passord er påkrevd'),
});
export type LoginInput = z.infer<typeof loginSchema>;

/** Self-service password change: the signed-in user proves the old, sets a new. */
export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Nåværende passord er påkrevd'),
  newPassword: z.string().min(10, 'Passordet må være minst 10 tegn'),
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

/** Forgot-password: ask for a reset link by e-mail. */
export const forgotPasswordSchema = z.object({
  email: z.string().email('Ugyldig e-postadresse'),
});
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

/** Forgot-password: spend the token from the link and set a new password. */
export const resetPasswordSchema = z.object({
  token: z.string().min(1, 'Token mangler'),
  newPassword: z.string().min(10, 'Passordet må være minst 10 tegn'),
});
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

// ── Users ────────────────────────────────────────────────────────────────────

export const createUserSchema = z.object({
  name: z.string().min(2).max(120),
  email: z.string().email(),
  password: z.string().min(10, 'Passordet må være minst 10 tegn'),
  role: zUserRole.default('MEMBER'),
  title: z.string().max(120).optional(),
  avatarUrl: z.string().url().optional().nullable(),
});
export type CreateUserInput = z.infer<typeof createUserSchema>;

export const updateUserSchema = createUserSchema
  .omit({ password: true })
  .partial()
  // Title is nullable here, not just optional: omitting it means "unchanged",
  // null means "remove the title" (07 — 2.1.9).
  .extend({
    isActive: z.boolean().optional(),
    title: z.string().max(120).nullable().optional(),
    // Only a superadmin may set this (enforced in the route); a plain admin cannot.
    isSuperadmin: z.boolean().optional(),
  });
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

// ── Areas ────────────────────────────────────────────────────────────────────

export const createAreaSchema = z.object({
  name: z.string().min(2).max(120),
  slug: z
    .string()
    .min(2)
    .max(80)
    .regex(/^[a-z0-9-]+$/, 'Slug kan bare inneholde små bokstaver, tall og bindestrek'),
  type: zAreaType,
  company: zCompany,
  description: z
    .string()
    .max(8000, 'Beskrivelsen er for lang – maks 8000 tegn.')
    .optional()
    .nullable(),
  valueProposition: z
    .string()
    .max(4000, 'Verdiløftet er for langt – maks 4000 tegn.')
    .optional()
    .nullable(),
  targetAudience: z
    .string()
    .max(2000, 'Målgruppe-teksten er for lang – maks 2000 tegn.')
    .optional()
    .nullable(),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Farge må være en hex-kode, f.eks. #38BDF8')
    .default('#38BDF8'),
  ownerUserId: cuid,
  linkedUserId: cuid.optional().nullable(),
  // Rhythm profile (07 — 2.2.1). Null = the planner's shared default applies.
  cadenceDays: z.number().int().min(1).max(365).optional().nullable(),
  peakMonths: z.array(z.number().int().min(1).max(12)).max(12).optional().nullable(),
  peakCadenceDays: z.number().int().min(1).max(365).optional().nullable(),
  lowMonths: z.array(z.number().int().min(1).max(12)).max(12).optional().nullable(),
  lowCadenceDays: z.number().int().min(1).max(365).optional().nullable(),
  // Source governance (07 — kildestyring). Default on: search beyond the list.
  allowExternalSources: z.boolean().optional(),
});
export type CreateAreaInput = z.infer<typeof createAreaSchema>;

export const updateAreaSchema = createAreaSchema.partial().extend({
  isActive: z.boolean().optional(),
});
export type UpdateAreaInput = z.infer<typeof updateAreaSchema>;

export const areaMemberSchema = z.object({
  userId: cuid,
  role: zAreaMemberRole.default('REMINDER_LIST'),
});
export type AreaMemberInput = z.infer<typeof areaMemberSchema>;

/**
 * The descriptive fields anyone who works in an area may edit — the copy that
 * shapes what Nova writes. Structure (slug, company, cadence, sources) stays
 * with admins (dokumentasjon/BESLUTNINGER.md, fase 3).
 */
export const updateAreaProfileSchema = z.object({
  description: z.string().max(8000, 'Beskrivelsen er for lang – maks 8000 tegn.').nullish(),
  valueProposition: z.string().max(4000, 'Verdiløftet er for langt – maks 4000 tegn.').nullish(),
  targetAudience: z.string().max(2000, 'Målgruppe-teksten er for lang – maks 2000 tegn.').nullish(),
  // Source governance is part of what an area's people tend (07 — kildestyring).
  allowExternalSources: z.boolean().optional(),
});
export type UpdateAreaProfileInput = z.infer<typeof updateAreaProfileSchema>;

// ── Area sources (07 — kildestyring) ─────────────────────────────────────────

export const createAreaSourceSchema = z.object({
  label: z.string().min(1, 'Navn er påkrevd').max(160),
  url: z.string().url('Må være en fullstendig URL, f.eks. https://digdir.no').max(1000),
  notes: z.string().max(2000).optional().nullable(),
});
export type CreateAreaSourceInput = z.infer<typeof createAreaSourceSchema>;

export const updateAreaSourceSchema = createAreaSourceSchema.partial().extend({
  isActive: z.boolean().optional(),
});
export type UpdateAreaSourceInput = z.infer<typeof updateAreaSourceSchema>;

/** What the scout reports after using a source outside the curated list. */
export const recordDiscoveredSourceSchema = z.object({
  url: z.string().url().max(1000),
  label: z.string().max(160).optional().nullable(),
});
export type RecordDiscoveredSourceInput = z.infer<typeof recordDiscoveredSourceSchema>;

// ── Channels ─────────────────────────────────────────────────────────────────

export const createChannelSchema = z.object({
  name: z.string().min(2).max(120),
  type: zChannelType,
  company: zCompany.default('BEGGE'),
  config: z.record(z.unknown()).default({}),
  publishMode: zPublishMode.default('MANUAL_PREPARED'),
  isActive: z.boolean().default(true),
});
export type CreateChannelInput = z.infer<typeof createChannelSchema>;

export const updateChannelSchema = createChannelSchema.partial();
export type UpdateChannelInput = z.infer<typeof updateChannelSchema>;

// ── Content ──────────────────────────────────────────────────────────────────

export const createContentSchema = z.object({
  title: z.string().min(3).max(240),
  areaId: cuid,
  pillar: zPillar,
  format: zContentFormat,
  summary: z.string().max(4000).optional().nullable(),
  body: z.string().optional().nullable(),
  ownerUserId: cuid.optional().nullable(),
  draftDeadline: isoDate.optional().nullable(),
  publishAt: isoDate.optional().nullable(),
  /** Date of execution for events (webinar/arrangement). Optional for any format. */
  eventDate: isoDate.optional().nullable(),
  agentRationale: z.string().max(8000).optional().nullable(),
  channelIds: z.array(cuid).default([]),
});
export type CreateContentInput = z.infer<typeof createContentSchema>;

export const updateContentSchema = createContentSchema.partial().extend({
  changeNote: z.string().max(500).optional(),
  /**
   * A debounced background save from the editor. It keeps the body current so
   * nothing is lost when switching tabs or closing the drawer, without cutting a
   * new version or logging an edit each time the way an explicit save does.
   */
  autosave: z.boolean().optional(),
});
export type UpdateContentInput = z.infer<typeof updateContentSchema>;

export const contentFilterSchema = z.object({
  company: zCompany.optional(),
  areaId: cuid.optional(),
  pillar: zPillar.optional(),
  status: z.union([zContentStatus, z.array(zContentStatus)]).optional(),
  channelId: cuid.optional(),
  ownerUserId: cuid.optional(),
  source: z.enum(['AGENT', 'HUMAN']).optional(),
  /** Only items a human has asked Nova to draft (07 — 2.1.5). */
  draftRequested: z.coerce.boolean().optional(),
  /** Leaves out everything the seed script fabricated (07 — 2.1.8). */
  excludeSeed: z.coerce.boolean().optional(),
  /** Leaves out the retired statuses (avvist, kansellert, utgått) unless a status is asked for. */
  activeOnly: z.coerce.boolean().optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
  search: z.string().max(120).optional(),
  overdueOnly: z.coerce.boolean().optional(),
  take: z.coerce.number().int().min(1).max(500).default(200),
  skip: z.coerce.number().int().min(0).default(0),
});
export type ContentFilter = z.infer<typeof contentFilterSchema>;

/**
 * Persist a manual within-column order on the board: the full, ordered list of
 * card ids in one status column. The server reindexes them to ascending
 * `boardOrder` values (redplan-ux fix #5).
 */
export const reorderContentSchema = z.object({
  status: zContentStatus,
  orderedIds: z.array(cuid).min(1).max(500),
});
export type ReorderContentInput = z.infer<typeof reorderContentSchema>;

export const rejectContentSchema = z.object({
  reason: z.string().min(3, 'Skriv en kort begrunnelse').max(1000),
});

/** Accepting a pre-forslag onto the board — an optional, positive note. */
export const acceptProposalSchema = z.object({
  comment: z.string().max(2000).optional().nullable(),
});
export type AcceptProposalInput = z.infer<typeof acceptProposalSchema>;

/** Discarding a pre-forslag — an optional «dette skal vi lære av»-note. */
export const discardProposalSchema = z.object({
  reason: z.string().max(2000).optional().nullable(),
});
export type DiscardProposalInput = z.infer<typeof discardProposalSchema>;

/** A person added to a post besides the owner. */
export const addContributorSchema = z.object({
  userId: cuid,
});
export type AddContributorInput = z.infer<typeof addContributorSchema>;

/** An internal comment on a post. */
export const createCommentSchema = z.object({
  body: z.string().min(1, 'Skriv en kommentar').max(8000),
});
export type CreateCommentInput = z.infer<typeof createCommentSchema>;

/** An image/graphics order on a post. */
export const createContentAssetSchema = z.object({
  brief: z.string().min(1, 'Beskriv hva som skal lages').max(4000),
});
export type CreateContentAssetInput = z.infer<typeof createContentAssetSchema>;

/** Editing the brief of an existing order. */
export const updateContentAssetSchema = z.object({
  brief: z.string().min(1, 'Beskriv hva som skal lages').max(4000),
});
export type UpdateContentAssetInput = z.infer<typeof updateContentAssetSchema>;

export const scheduleContentSchema = z.object({
  publishAt: isoDate,
  channelIds: z.array(cuid).min(1, 'Velg minst én kanal'),
  /** Skip the desktest and schedule anyway (the owner takes responsibility). */
  override: z.boolean().optional(),
});

export const confirmPublishedSchema = z.object({
  channelTargetId: cuid.optional(),
  externalRef: z.string().url('Lim inn en gyldig lenke').optional().nullable(),
  publishedAt: isoDate.optional(),
});

export const cancelContentSchema = z.object({
  reason: z.string().max(1000).optional(),
});

/**
 * «Be Nova skrive». An optional instruction steers a rewrite («behold strukturen,
 * gjør tonen mer ydmyk»); with an existing body Nova revises it rather than
 * starting from scratch.
 */
export const writeDraftSchema = z.object({
  instruction: z.string().max(500).optional(),
});

/**
 * Undo a mistaken «Bekreft publisert». `confirmedNotPublished` forces the person
 * to confirm nothing actually went out manually before the item leaves PUBLISHED.
 */
export const revertPublishedSchema = z.object({
  to: z.enum(['SCHEDULED', 'DRAFT', 'SUGGESTED']),
  confirmedNotPublished: z.literal(true),
});

/**
 * «Foreslå kanalversjoner». Nova fills in channel-adapted copy per channel
 * target. By default it only fills targets that are still empty; `force` lets it
 * regenerate every target (overwriting any the user has edited).
 */
export const suggestChannelVariantsSchema = z.object({
  force: z.boolean().optional(),
});
export type SuggestChannelVariantsInput = z.infer<typeof suggestChannelVariantsSchema>;

export const channelTargetUpdateSchema = z.object({
  adaptedBody: z.string().max(20000).optional().nullable(),
  status: zChannelTargetStatus.optional(),
  externalRef: z.string().max(500).optional().nullable(),
});

// ── Agents ───────────────────────────────────────────────────────────────────

export const updateAgentSchema = z.object({
  isEnabled: z.boolean().optional(),
  scheduleCron: z.string().max(120).optional(),
  config: z.record(z.unknown()).optional(),
});
export type UpdateAgentInput = z.infer<typeof updateAgentSchema>;

export const startAgentRunSchema = z.object({
  agentKey: zAgentKey,
  trigger: zAgentRunTrigger.default('SCHEDULE'),
  summary: z.string().max(2000).optional(),
});

export const patchAgentRunSchema = z.object({
  status: zAgentRunStatus.optional(),
  summary: z.string().max(4000).optional(),
  /** Appended to the structured run log rather than replacing it. */
  logEntry: z
    .object({
      step: z.string().max(200),
      detail: z.string().max(8000).optional(),
      tokensIn: z.number().int().nonnegative().optional(),
      tokensOut: z.number().int().nonnegative().optional(),
    })
    .optional(),
  error: z.string().max(8000).optional().nullable(),
});

export const runAgentNowSchema = z.object({
  note: z.string().max(500).optional(),
});

// ── Signals (scout) ──────────────────────────────────────────────────────────

export const createSignalSchema = z.object({
  title: z.string().min(3).max(240),
  summary: z.string().max(4000),
  sourceUrl: z.string().url('Signaler må ha en ekte kildelenke'),
  sourceName: z.string().max(160).optional().nullable(),
  areaId: cuid.optional().nullable(),
  suggestedAngle: z.string().max(2000).optional().nullable(),
  relevance: z.number().int().min(1).max(5).default(3),
});
export type CreateSignalInput = z.infer<typeof createSignalSchema>;

// ── KPI ──────────────────────────────────────────────────────────────────────

export const createKpiSchema = z.object({
  name: z.string().min(2).max(160),
  description: z.string().max(2000).optional().nullable(),
  unit: z.string().max(40).default('antall'),
  targetValue: z.number(),
  targetDate: isoDate.optional().nullable(),
  areaId: cuid.optional().nullable(),
  source: zKpiSource.default('MANUAL'),
});
export type CreateKpiInput = z.infer<typeof createKpiSchema>;

export const updateKpiSchema = createKpiSchema.partial().extend({
  isActive: z.boolean().optional(),
});

export const createKpiDatapointSchema = z.object({
  date: isoDate,
  value: z.number(),
  sourceDetail: z.string().max(500).optional().nullable(),
});
export type CreateKpiDatapointInput = z.infer<typeof createKpiDatapointSchema>;

// ── Notifications ────────────────────────────────────────────────────────────

export const createNotificationSchema = z.object({
  userId: cuid,
  areaId: cuid.optional().nullable(),
  contentItemId: cuid.optional().nullable(),
  type: zNotificationType,
  channel: zNotificationChannel.default('INAPP'),
  subject: z.string().min(2).max(240),
  body: z.string().max(20000),
});
export type CreateNotificationInput = z.infer<typeof createNotificationSchema>;

export const respondNotificationSchema = z.object({
  response: z.record(z.unknown()).optional(),
});

// ── Activity ─────────────────────────────────────────────────────────────────

export const activityFilterSchema = z.object({
  excludeSeed: z.coerce.boolean().optional(),
  actorType: z.enum(['USER', 'AGENT', 'SYSTEM']).optional(),
  entityType: z.string().max(60).optional(),
  entityId: cuid.optional(),
  areaId: cuid.optional(),
  search: z.string().max(120).optional(),
  take: z.coerce.number().int().min(1).max(200).default(50),
  skip: z.coerce.number().int().min(0).default(0),
});
export type ActivityFilter = z.infer<typeof activityFilterSchema>;

// ── Dashboard ────────────────────────────────────────────────────────────────

export const dashboardQuerySchema = z.object({
  company: zCompany.optional(),
  areaId: cuid.optional(),
  excludeSeed: z.coerce.boolean().optional(),
});
export type DashboardQuery = z.infer<typeof dashboardQuerySchema>;

// ── Brand: tone of voice and profile material ────────────────────────────────

export const zBrandScope = z.enum(BRAND_SCOPES);
export const zProfileDocumentType = z.enum(PROFILE_DOCUMENT_TYPES);
export const zProfileDocumentKind = z.enum(PROFILE_DOCUMENT_KINDS);

/**
 * Which level is being addressed. GLOBAL takes neither; COMPANY takes a company;
 * AREA takes an area. Validated here so a half-specified level cannot reach the
 * database and quietly become something else.
 */
export const brandScopeSelectorSchema = z
  .object({
    scope: zBrandScope,
    company: zCompany.optional(),
    areaId: cuid.optional(),
    channelId: cuid.optional(),
  })
  .refine((v) => (v.scope === 'COMPANY' ? Boolean(v.company) : !v.company), {
    message: 'Selskap må oppgis for selskapsnivå, og bare der.',
    path: ['company'],
  })
  .refine((v) => (v.scope === 'AREA' ? Boolean(v.areaId) : !v.areaId), {
    message: 'Område må oppgis for områdenivå, og bare der.',
    path: ['areaId'],
  })
  .refine((v) => (v.scope === 'CHANNEL' ? Boolean(v.channelId) : !v.channelId), {
    message: 'Kanal må oppgis for kanalnivå, og bare der.',
    path: ['channelId'],
  });
export type BrandScopeSelector = z.infer<typeof brandScopeSelectorSchema>;

const wordList = z.array(z.string().min(1).max(80)).max(200);

export const updateBrandProfileSchema = z.object({
  toneOfVoice: z.string().max(8000).nullish(),
  positioning: z.string().max(4000).nullish(),
  wordsWeUse: wordList.optional(),
  wordsWeAvoid: wordList.optional(),
  goodExamples: z.string().max(8000).nullish(),
  badExamples: z.string().max(8000).nullish(),
});
export type UpdateBrandProfileInput = z.infer<typeof updateBrandProfileSchema>;

const documentFields = {
  title: z.string().min(2).max(200),
  docType: zProfileDocumentType.default('ANNET'),
  kind: zProfileDocumentKind,
  url: z.string().url().max(1000).nullish(),
  content: z.string().max(100_000).nullish(),
  note: z.string().max(2000).nullish(),
  agentsShouldRead: z.boolean().default(false),
  sortOrder: z.number().int().min(0).max(999).default(0),
};

export const createProfileDocumentSchema = z
  .object({
    scope: zBrandScope,
    company: zCompany.optional(),
    areaId: cuid.optional(),
    /** Set by the upload endpoint, not by hand. */
    fileKey: z.string().max(255).optional(),
    fileName: z.string().max(255).optional(),
    fileSize: z.number().int().min(0).optional(),
    mimeType: z.string().max(150).optional(),
    ...documentFields,
  })
  .refine((v) => v.kind !== 'LINK' || Boolean(v.url), {
    message: 'En lenke trenger en adresse.',
    path: ['url'],
  })
  .refine((v) => v.kind !== 'INLINE' || Boolean(v.content?.trim()), {
    message: 'Et dokument skrevet i Nova trenger tekst.',
    path: ['content'],
  })
  .refine((v) => v.kind !== 'FILE' || Boolean(v.fileKey), {
    message: 'Last opp filen først.',
    path: ['fileKey'],
  });
export type CreateProfileDocumentInput = z.infer<typeof createProfileDocumentSchema>;

export const updateProfileDocumentSchema = z.object({
  title: documentFields.title.optional(),
  docType: zProfileDocumentType.optional(),
  url: z.string().url().max(1000).nullish(),
  content: z.string().max(100_000).nullish(),
  note: z.string().max(2000).nullish(),
  agentsShouldRead: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(999).optional(),
});
export type UpdateProfileDocumentInput = z.infer<typeof updateProfileDocumentSchema>;

// ── Salg / Anbudsradar ───────────────────────────────────────────────────────

export const zTenderNoticeType = z.enum(TENDER_NOTICE_TYPES);
export const zTenderStatus = z.enum(TENDER_STATUSES);
export const zTenderNoBidReason = z.enum(TENDER_NO_BID_REASONS);
export const zTenderBuyerType = z.enum(TENDER_BUYER_TYPES);

const keywordList = z.array(z.string().trim().min(1).max(120)).max(60).default([]);
const cpvList = z
  .array(
    z
      .string()
      .trim()
      .regex(/^\d{2,8}$/, 'CPV-koden må være 2–8 sifre.'),
  )
  .max(60)
  .default([]);

export const createWatchProfileSchema = z.object({
  name: z.string().trim().min(2).max(160),
  productArea: z.string().trim().min(1).max(160),
  description: z.string().max(2000).nullish(),
  cpvCodes: cpvList,
  includeKeywords: keywordList,
  excludeKeywords: keywordList,
  buyerTypes: z.array(zTenderBuyerType).max(10).default([]),
  noticeTypes: z.array(zTenderNoticeType).max(5).default([]),
  notifyUserIds: z.array(cuid).max(50).default([]),
  minRelevance: z.number().int().min(0).max(100).default(50),
  isActive: z.boolean().default(true),
});
export type CreateWatchProfileInput = z.infer<typeof createWatchProfileSchema>;

export const updateWatchProfileSchema = createWatchProfileSchema.partial();
export type UpdateWatchProfileInput = z.infer<typeof updateWatchProfileSchema>;

/**
 * Change a tender's status. NO_BID requires a reason from the fixed list — it is
 * learning data, not free text; the refine enforces it before it reaches the API.
 */
export const updateTenderStatusSchema = z
  .object({
    status: zTenderStatus,
    noBidReason: zTenderNoBidReason.nullish(),
    noBidNote: z.string().max(2000).nullish(),
  })
  .refine((v) => v.status !== 'NO_BID' || Boolean(v.noBidReason), {
    message: 'No-bid krever en årsak fra listen.',
    path: ['noBidReason'],
  });
export type UpdateTenderStatusInput = z.infer<typeof updateTenderStatusSchema>;

export const assignTenderSchema = z.object({ ownerUserId: cuid.nullable() });
export type AssignTenderInput = z.infer<typeof assignTenderSchema>;

export const tenderFilterSchema = z.object({
  status: zTenderStatus.optional(),
  noticeType: zTenderNoticeType.optional(),
  productArea: z.string().max(160).optional(),
  search: z.string().max(160).optional(),
  /** Include below-threshold notices (hidden from the main list by default). */
  includeBelowThreshold: z.coerce.boolean().default(false),
  /** Include closed tenders (vunnet/tapt/avlyst). */
  includeClosed: z.coerce.boolean().default(false),
  sort: z.enum(['relevance', 'deadline', 'published']).default('relevance'),
  take: z.coerce.number().int().min(1).max(200).default(100),
  skip: z.coerce.number().int().min(0).default(0),
});
export type TenderFilterInput = z.infer<typeof tenderFilterSchema>;

// ── Salg / Marked og kunder (SALES-modulen, fase S2) ─────────────────────────

export const zOrganizationType = z.enum(ORGANIZATION_TYPES);
export const zProductHoldingStatus = z.enum(PRODUCT_HOLDING_STATUSES);
export const zHoldingConfidence = z.enum(HOLDING_CONFIDENCES);
export const zInteractionType = z.enum(INTERACTION_TYPES);

/** Filters for the white-space organisation list. */
export const organizationFilterSchema = z.object({
  marketId: cuid.optional(),
  type: zOrganizationType.optional(),
  fylke: z.string().max(120).optional(),
  /** Filter by a holding's current solution / competitor (e.g. a CMS vendor). */
  competitor: z.string().max(200).optional(),
  /** Filter to organisations that have a holding in this status. */
  holdingStatus: zProductHoldingStatus.optional(),
  productId: cuid.optional(),
  /** Filter to organisations holding at least one of these products (comma-separated). */
  productIds: z.string().max(600).optional(),
  search: z.string().max(160).optional(),
  /** Only organisations whose next contract expiry is within this many days. */
  expiringWithinDays: z.coerce.number().int().min(1).max(3650).optional(),
  sort: z.enum(['name', 'expiry', 'population']).default('name'),
  take: z.coerce.number().int().min(1).max(500).default(100),
  skip: z.coerce.number().int().min(0).default(0),
});
export type OrganizationFilterInput = z.infer<typeof organizationFilterSchema>;

/** Date-only strings (YYYY-MM-DD) or full ISO; the API anchors them at noon. */
const dateString = z
  .string()
  .trim()
  .min(1)
  .refine((v) => !Number.isNaN(Date.parse(v)), 'Ugyldig dato.');

export const createOrganizationSchema = z.object({
  name: z.string().trim().min(1).max(200),
  type: zOrganizationType.default('KOMMUNE'),
  orgNr: z.string().trim().max(20).nullish(),
  kommunenummer: z.string().trim().max(8).nullish(),
  fylke: z.string().trim().max(120).nullish(),
  population: z.coerce.number().int().min(0).nullish(),
  website: z.string().trim().max(500).nullish(),
  marketId: cuid.nullish(),
});
export type CreateOrganizationInput = z.infer<typeof createOrganizationSchema>;

export const updateOrganizationSchema = createOrganizationSchema.partial();
export type UpdateOrganizationInput = z.infer<typeof updateOrganizationSchema>;

export const upsertHoldingSchema = z.object({
  productId: cuid,
  status: zProductHoldingStatus,
  competitor: z.string().trim().max(200).nullish(),
  contractStart: dateString.nullish(),
  contractEnd: dateString.nullish(),
  hasOptions: z.boolean().default(false),
  optionsNote: z.string().trim().max(400).nullish(),
  annualValue: z.coerce.number().min(0).nullish(),
  currency: z.string().trim().max(8).nullish(),
  source: z.string().trim().max(200).nullish(),
  sourceDate: dateString.nullish(),
  confidence: zHoldingConfidence.nullish(),
  liveVerified: z.boolean().default(false),
  dataNote: z.string().trim().max(4000).nullish(),
});
export type UpsertHoldingInput = z.infer<typeof upsertHoldingSchema>;

export const createInteractionSchema = z.object({
  productId: cuid.nullish(),
  type: zInteractionType,
  note: z.string().trim().min(1).max(4000),
  occurredAt: dateString,
  nextStep: z.string().trim().max(2000).nullish(),
  nextStepDate: dateString.nullish(),
});
export type CreateInteractionInput = z.infer<typeof createInteractionSchema>;

// ── SoMe-overvåkning (Marked) ────────────────────────────────────────────────

export const createSocialAccountSchema = z.object({
  platform: zSocialPlatform,
  kind: zSocialAccountKind,
  name: z.string().trim().min(2).max(160),
  url: z.string().url('Lenken må være en gyldig nettadresse').max(1000).optional().nullable(),
  company: zCompany.default('BEGGE'),
  ownerUserId: cuid.optional().nullable(),
  areaId: cuid.optional().nullable(),
  channelId: cuid.optional().nullable(),
  isActive: z.boolean().default(true),
  note: z.string().max(2000).optional().nullable(),
});
export type CreateSocialAccountInput = z.infer<typeof createSocialAccountSchema>;

export const updateSocialAccountSchema = createSocialAccountSchema.partial();
export type UpdateSocialAccountInput = z.infer<typeof updateSocialAccountSchema>;

/**
 * One month's numbers for an account. Upserted on `(accountId, period)`, so
 * re-entering a month updates rather than duplicates. `period` is normalised to
 * the first day of its month on the API.
 */
export const upsertSocialMetricSchema = z.object({
  period: isoDate,
  followers: z.number().int().min(0).optional().nullable(),
  reach: z.number().int().min(0).optional().nullable(),
  engagements: z.number().int().min(0).optional().nullable(),
  posts: z.number().int().min(0).optional().nullable(),
  source: zSocialMetricSource.default('MANUAL'),
  note: z.string().max(2000).optional().nullable(),
});
export type UpsertSocialMetricInput = z.infer<typeof upsertSocialMetricSchema>;
